import type { Article } from './types'

/**
 * Help for the Certifications module (/certs/*).
 *
 * Every label quoted in bold is copied from the screen it describes. When a
 * screen's wording changes, change the article in the same commit.
 */
export const CERT_ARTICLES: Article[] = [
  // ------------------------------------------------------------------ start
  {
    slug: 'certs-overview',
    title: 'Certifications: start here',
    section: 'Certifications',
    audience: 'certs',
    summary: 'What the Certifications module tracks, who sees which screens, and where to begin.',
    body: `Certifications keeps track of the cards and trainings field staff hold — OSHA, forklift, first aid, fit tests and so on — and checks them against what NW Linings requires.

> Certifications is being tested and is only switched on for some people right now. If you see **Certifications aren't switched on for your account.**, it has not been opened to you yet.

## How it works in a sentence

The office records each person's cards. You decide who must hold what on **Requirements**. The portal works out every status fresh each time a screen opens, so a card that lapsed overnight shows **Expired** the next morning without anyone doing anything.

## Who sees what

| Role | Menu items | What they can do |
| --- | --- | --- |
| Technician | **Mine** | See their own cards and what they still need. Read only. |
| Supervisor | **My crew**, **Mine** | See their crew's status, read only. Gets a warning on New crew day. |
| Office | **Employees**, **Requirements**, **Setup** | Record, renew, correct, verify and void cards. Add and close requirements. Set alert email addresses. Look at the Setup screens and expiry alerts. |
| Admin | Everything Office has | Also: see and edit medical cards, change types, categories and relations, mark **Needs re-evaluation**, and change or send expiry alerts. |

Office and Admin reach anyone — themselves included — from **Employees**, so **My crew** and **Mine** don't show on their menu.

## Medical cards

Some types, like medical clearance, are **Restricted (medical)**. Below Admin, everyone sees only whether they are in order — no dates, numbers or notes. Even the person who holds the card sees only the status.

## Where to begin (office)

1. Check the list of [types](/help/certs-types) and their validity periods.
2. Add cards on each [person's page](/help/certs-person).
3. Add [requirements](/help/certs-requirements). Until there is one, nobody is counted as missing anything.
4. Watch the [Employees grid](/help/certs-employee-grid) and the [Problems screen](/help/certs-problems).

What each status means is on [Statuses and what to do](/help/certs-statuses).

> The portal doesn't store copies of cards. Keep the paper or scan wherever you keep it today.`,
  },

  // ------------------------------------------------------------------ /certs
  {
    slug: 'certs-employee-grid',
    title: 'The Employees grid',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Everyone in the field against every certification, with the most urgent thing in each box.',
    routes: ['/certs'],
    body: `**Employee certifications** shows field staff down the side and every active certification across the top. Each box shows the most urgent thing about it. Hover over a box for the detail. Click a person to open [their page](/help/certs-person).

## Reading a box

| Box | Means |
| --- | --- |
| **Missing** | Required of this person and nothing that counts is on file |
| **Expired** | Their card has expired |
| **Blocked** | A prerequisite is not in order |
| **Re-eval** | Flagged for re-evaluation; does not count until cleared |
| **Pending** | Recorded but not yet verified |
| **Soon** | Inside its warning window |
| **OK** | Current |
| **Equiv** | Not held, but a requirement is met by an equivalent (OSHA 30 for OSHA 10) |
| **Grace** | A new hire still inside the grace period |
| blank | Not held and not required |

A small **ᴿ** after a heading marks a medical (restricted) type. Those boxes show the status only, never dates, unless you are an Admin.

## Narrowing the list

- **Person** — type a name or F-number.
- **Show** — **Everyone**, **Missing a required cert**, **Has an expired cert**, **Blocked or needs re-evaluation**, or **Expiring soon**.
- **Category**, **Crew**, **Classification** and **Location** narrow it further.
- **As of** shows the grid on another date, for example to see who will have lapsed by the start of a job.

Under the filters a line counts what is showing: people, **missing**, **expired**, **blocked** and **expiring soon**.

## Scrolling right

There are more certifications than fit on the screen. When some are hidden, **More certifications to the right →** shows above the grid; click it to slide across. Names stay pinned on the left. On a Mac, where scroll bars hide, this link is the easiest way across.

## Export CSV

**Export CSV** downloads what is showing, with filters applied, including expiry dates where you are allowed to see them.`,
  },

  // --------------------------------------------------------- /certs/people
  {
    slug: 'certs-person',
    title: "A person's certifications",
    section: 'Certifications',
    audience: 'certs',
    summary: 'Add, renew, correct, verify and void cards, and see how each requirement is met.',
    routes: ['/certs/people'],
    body: `A person's page answers "is this person OK to work?" first, then shows what they hold and everything that came before.

## Requirements

Each rule that applies to the person, with **Why it applies** (for example a classification, or a job they worked) and a **Status** such as **Met** or **Missing**. See [Statuses and what to do](/help/certs-statuses).

## Current certifications

Columns: **Certification**, **Status**, **Issued**, **Expiry**, **Number / provider**, **Recorded**. **Recorded** shows who entered it and whether it is **Not yet verified** or **Verified**.

- **Add a certification** — pick the **Certification**, enter **Issued**, then the expiry. For a fixed-period type the expiry is worked out for you; tick **The card shows a different date** if it differs, and say why. For a card-date type, enter **Expires (as printed on the card)**. Add **Card or certificate number**, **Trained / issued by** and a **Note** if useful. Press **Save**.
- **Renew** — records the new card. The earlier card is kept in the history.
- **Correct** — fixes a typo. Changing a date asks **Why the date is changing**. Press **Save correction**.
- **Verify** — shows only on a record **Awaiting verification**. Press it once you have seen the card.
- **Void** — for a record that should never have been entered. Say why under **Why is this record being voided?** and press **Void this record**. The record stays in the history; if there is an earlier card, it becomes current again.
- **Needs re-evaluation** (Admin) — say what triggered it and press **Save**. Until cleared with **Clear re-evaluation**, the card does not count.

Nothing is ever deleted. Medical cards can only be added or changed by an Admin.

## History

Earlier and voided records, each marked **Superseded** or **Voided**, with who voided it and why.

## Expiry alerts card

Shows where this person's alert emails go. Press **Change** to set an **Alert email**, or tick **Don't email this person**. Leave it blank to use their sign-in address. With no address, alerts reach the office summary only. See [Expiry alerts](/help/certs-expiry-alerts).

## Looking back

**As of** shows the page on an earlier date. Cards issued after that date are not counted, and nothing can be changed while looking back.`,
  },

  // --------------------------------------------------- /certs/requirements
  {
    slug: 'certs-requirements',
    title: 'Requirements: who must hold what',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Set which certifications are required of whom, from all field staff down to one job.',
    routes: ['/certs/requirements'],
    body: `Requirements are NW Linings' call. They are what turns "has a card" into "is missing a card", and they drive the counts on Problems.

> Until the first requirement is saved, nobody is counted as missing anything. The moment it is saved, everyone it applies to with no card on file shows on Problems as missing it. The screen warns you before the first one.

## Add a requirement

1. **Certification** — what is required.
2. **Required of** — who it applies to:
   - **All field staff** — every active field employee.
   - **A classification** — pick the **Classification**.
   - **A kind of job** — pick from **Prevailing wage jobs**, **Davis-Bacon jobs**, **Union jobs**, **Public works jobs**, **Certified payroll jobs** or **OCIP jobs**.
   - **A customer** — enter **Division-customer, as in Sage**, for example 00-ABC123.
   - **A job type (Sage UDF)** — enter **Job type, as written in Sage**.
   - **One job** — enter the **Job number**, for example N25197.
3. **New-hire grace (days)** — how long a new hire has before a missing card counts against them. Counted from their hire date. Leave at 0 for none.
4. **Starting (blank = today)** — when the rule takes effect.
5. **Note** — for example "Micron site badge".
6. Press **Add requirement**.

## Who a job rule covers

A company-wide or classification rule covers active field staff. A job, customer or kind-of-job rule covers anyone recorded on a matching job this pay week or last. The portal has no forward schedule, so the [crew-day warning](/help/certs-crew-day-warning) covers the moment someone is about to be recorded on a job.

## Closing a requirement

Press **Close** on its row. It stops counting but stays in the history. Tick **Show closed ones too** to see closed rules, with the date each closed. Nothing is deleted.

## The list

Columns: **Certification**, **Applies to**, **New-hire grace**, **From**, **Note**. Office and Admin can add and close requirements.`,
  },

  // ---------------------------------------------------------- /certs/types
  {
    slug: 'certs-types',
    title: 'Certification types',
    section: 'Certifications',
    audience: 'certs',
    summary: 'The list of certifications, how long each lasts, when to warn, and which are medical.',
    routes: ['/certs/types'],
    body: `**Types** lists every certification the portal knows about, grouped by category. Office can look; only an Admin can add or change a type.

> Validity periods were set up as common defaults and are for NW Linings to confirm. Changing a period does not rewrite cards already on file.

## The list

Columns: **Type**, **Short**, **Expiry**, **Warn**, **Extra fields**. A **restricted** badge marks a medical type; **inactive** marks one no longer offered.

## Add a type or Edit

Press **Add a type**, or **Edit** on a row.

- **Name** and **Short name (matrix heading)** — the short name is the column heading on the Employees grid.
- **Category** — which group it belongs to.
- **Expiry** — **Fixed period from issue** (then set **Lasts (months)**), **Date on the card**, or **Does not expire**.
- **Warn this many days ahead** — when the card turns **Expiring soon**, and when the first expiry alert would go.
- **Restricted (medical)** — **Same as its category**, **Yes — Admin only sees detail**, or **No**.
- **Issuing body** and **Description** — for reference.
- **Offered for new records** — untick to stop a type being chosen for new cards. Nothing is deleted.
- **Extra fields this type records** — **Add a field** for anything else the card carries. Give it a label and tick **required** if it must be filled in.

Press **Save type**.

## Medical (restricted) types

Below Admin, a medical type shows the status only: no dates, numbers, provider or notes. Office cannot add or change medical cards either, because they could not see what they had typed. Only an Admin records them.`,
  },

  // ---------------------------------------------------------- /certs/setup
  {
    slug: 'certs-categories-relations',
    title: 'Categories and relations',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Group types into categories, and set which cards require or count as others.',
    routes: ['/certs/setup'],
    body: `**Categories & relations** has two tabs. Office can look; only an Admin can make changes.

## Categories

Categories group the types on the Employees grid and its **Category** filter. Columns: **Category**, **Restricted**, **Order**, **Active**.

A restricted category hides the detail of every type in it from everyone but Admins — only the status shows. A single type can override its category on [Types](/help/certs-types).

To add one, type the **New category**, tick **Restricted** if it holds medical types, and press **Add category**. Untick **Active** to retire one.

## Relations

Relations connect two types. There are two kinds:

| Relation | Means | Example |
| --- | --- | --- |
| **requires** | The first is blocked unless the second is current **and** was in place when the first was issued | A fit test requires medical clearance first |
| **counts as** | Holding the first meets a requirement for the second | OSHA 30 counts as OSHA 10 |

The order matters for **requires**. OSHA treats a fit test given before medical clearance as invalid even if it was passed, so a card issued before its prerequisite shows **Blocked by a prerequisite** even once the prerequisite is in place.

Relations go one level only.

To add one, pick the first **Certification**, the **Relation**, the second **Certification**, add a **Note (e.g. the OSHA citation)**, and press **Add relation**.

## Closing a relation

Press **Close** on its row. It stops applying but stays in the list; tick **Show closed ones too** to see it. **Since** shows when it was added, or **Seeded** for ones set up at the start.`,
  },

  // -------------------------------------------------- /certs/notifications
  {
    slug: 'certs-expiry-alerts',
    title: 'Expiry alerts by email',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Built but switched off: who would be emailed about expiring cards, and how to preview it.',
    routes: ['/certs/notifications'],
    body: `**Expiry alerts** can email an employee about their own cards, and send an office summary, as cards near expiry.

> Expiry alerts are built but switched **off**. Who receives the office summary, and when alerts go out, have not been decided yet. Until then nothing is emailed to anyone, and you can preview safely.

Office can look at this screen. Only an Admin can change settings, recipients, or send.

## When an alert would go

- The first on the day a card enters its warning window (30 days unless the type says otherwise).
- Extra reminders at the days in **Reminders, days before expiry**, for example 14,7.
- Once when it expires, if **Send a notice when a certification expires (within 30 days of it lapsing)** is ticked.

Each card is emailed once per stage. Renewing it starts again. Medical cards are named without dates.

## Settings

- **Email employees about their own certifications**
- **Email the office summary to the recipients below**
- Press **Save settings**.

## Office summary recipients

Add a **Name** and **Email** and press **Add recipient**. **Switch off** stops one without removing them.

## Due now

A preview of what is due today. Columns: **Person**, **Certification**, **Status**, **Stage**, **Employee**. The **Employee** badge says **Will be emailed**, **Emailed earlier**, **No email on file**, **Opted out** or **Employee alerts off**. People who **Can't be emailed** are listed with a link to their page, where you can add an alert email.

**Emails that would go** lists each message. **Send now** sends exactly what is previewed, after asking you to confirm. It only works once a switch is on.

## Send a test email

Enter an address in **To** and press **Send test**. It proves email arrives, and works with both switches off.

**Sent log** lists everything sent.`,
  },

  // ----------------------------------------------------------- /certs/crew
  {
    slug: 'certs-my-crew',
    title: "My crew's certifications",
    section: 'Certifications',
    audience: 'certs',
    summary: "A supervisor's read-only view of who on the crew is good, expiring or missing something.",
    routes: ['/certs/crew'],
    body: `**Crew certifications** is the supervisor's view: you and your crew, one card per person. The top line counts people, how many **need attention**, and the date.

## Reading a card

Each person shows small counts such as **1 missing**, **2 expired**, **1 on hold**, **1 expiring**, **1 unchecked** or **5 good**. **(you)** marks your own card. **nothing on file** means no cards and nothing required.

Tap a person to see each certification and its status, for example **Current · until 14 Mar 2027** or **Missing — required**. Medical types are marked **· medical** and show only whether they are in order — no dates.

## Fixing a problem

This screen is read only. The office records and renews cards, so if someone is missing or expired, have them send their card in to the office.

## People on no crew

Tick **Also show people on no crew (floaters)** to check floaters you might pick up for a day. They appear under **On no crew**.

## No signal?

If the screen says **Couldn't load your crew. Check your signal and try again.**, tap **Try again** once you have signal.

See also the [warning on New crew day](/help/certs-crew-day-warning), and [what each status means](/help/certs-statuses).`,
  },

  // ----------------------------------------------------------- /certs/mine
  {
    slug: 'certs-mine',
    title: 'My certifications',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Your own cards, whether each is good and until when, and anything you still need.',
    routes: ['/certs/mine'],
    body: `**My certifications** shows the cards the office has on file for you, in plain words. It is read only.

## Needs attention

A red box at the top lists anything you are required to hold but don't have in order. For example:

- **None on file: bring your card to the office.**
- **...and yours has expired.**
- **...and yours isn't counting yet.**
- **You're new, so there's time, but get it done.** — you are a new hire inside the grace period.

## Each card

Each card shows its name, a badge and one line:

| Badge | What it means |
| --- | --- |
| **Good** | Current. Shows **Good until** a date, or **Good — does not expire**. |
| **Expiring** | Expires soon. Ask the office about renewing. |
| **Expired** | See the office. |
| **On hold** | Not counting yet — something it depends on isn't in place. See the office. |
| **Re-check** | Needs re-evaluation. See the office. |
| **Pending** | Waiting for the office to check it. |

Medical cards show only whether they are in order, with **Medical — the office keeps the details.**

**Required and covered** at the bottom lists requirements you already meet.

## Something wrong?

The office keeps these records. If a card is missing or a date is wrong, tell the office and bring the card in. If you see **No certifications on file**, bring in any cards you hold — OSHA, forklift, first aid.

If the screen can't load, tap **Try again** once you have signal.`,
  },

  // ------------------------------------------------- crew-day warning (no route)
  {
    slug: 'certs-crew-day-warning',
    title: 'The certification warning on New crew day',
    section: 'Certifications',
    audience: 'certs',
    summary: 'When you record a crew day, the portal warns if anyone ticked is missing a certification.',
    body: `When a supervisor fills in **New crew day**, the portal checks the people ticked against the job and date. If anyone is missing something, a box appears under the crew list headed **Certifications for this job:**.

> This is a warning, never a block. **You can still record them. The office sees this too.**

## What it checks

- Requirements for all field staff and for each person's classification.
- Whatever **this** job requires — a kind of job such as prevailing wage, union or OCIP, the customer, or the job itself.

It shows only people with something to report. People who are fully covered don't appear.

## Reading the lines

Each line names the person and what is wrong, worst first:

- **no** (type) **on file — required on this job** — nothing on file. If one was handed in but not yet checked, it adds **(one is waiting for the office to check)**.
- (type) **expired** (date) — the card has lapsed.
- (type) **doesn't count yet (a prerequisite or re-evaluation)**.
- (type) **expires** (date) — still good, but due soon.
- **new hire: needs** (type) **by** (date) — inside the new-hire grace period.

The box is yellow when someone is missing, expired or blocked, and blue when it is only expiring or grace-period notes. Medical types show no dates.

## What to do

Decide whether the person should work that job. Either way, the office sees the same people on [Problems](/help/certs-problems) and follows up. The office records and renews cards — supervisors don't.`,
  },

  // ------------------------------------------------- Problems (no route)
  {
    slug: 'certs-problems',
    title: 'Certification problems on the Problems screen',
    section: 'Certifications',
    audience: 'certs',
    summary: 'The certification checks that appear on the office Problems list and how to clear them.',
    body: `The office **Problems** screen includes certification checks alongside the time and pay ones. Each opens the person's page with **Open certifications**.

## The checks

| Problem | Means | What to do |
| --- | --- | --- |
| **Required certification expired** | The person is working without a card the job or company requires | Renew the card on their page |
| **Required certification missing** | Nothing on file for a card they must hold. May add "(one is awaiting verification)" | Add the card, or verify the one waiting |
| **Certification blocked by a prerequisite** | A prerequisite has lapsed, or was not in place when this card was issued | Fix the prerequisite; an out-of-order card usually needs redoing |
| **Certifications expiring soon** | Inside its warning window | Book the renewal before it lapses |
| **Re-evaluation required** | Flagged for re-evaluation; does not count until done | Arrange it; an Admin clears the flag |
| **Self-reported certifications awaiting verification** | Recorded but not checked by the office | Check the card, then **Verify** |

The two "required" checks only appear once at least one [requirement](/help/certs-requirements) exists. Before that, nobody is counted as missing anything.

For medical types the detail shows only the type and the problem — never dates.

## When a problem goes away

Problems are worked out fresh each time the list opens. Once the card is renewed, added or verified, the problem drops off on its own. Inactive employees are not listed.`,
  },

  // ------------------------------------------------------- reference
  {
    slug: 'certs-statuses',
    title: 'Certification statuses and what to do',
    section: 'Certifications',
    audience: 'certs',
    summary: 'Every card status and requirement outcome, in plain words, with the next step for each.',
    body: `The portal uses two kinds of status. A **card status** says what is true of one card. A **requirement outcome** says whether a person meets one rule. Both are worked out fresh each time a screen opens.

## Card statuses

| Status | Grid | Means | What to do |
| --- | --- | --- | --- |
| **Current** | **OK** | In date and in order | Nothing |
| **Expiring soon** | **Soon** | Within its warning window (30 days unless the type says otherwise). Still counts | Book the renewal |
| **Expired** | **Expired** | Past its expiry date. Doesn't count | Renew it |
| **Blocked by a prerequisite** | **Blocked** | A card it depends on has lapsed, or wasn't in place when this one was issued | Fix the prerequisite, or redo this card in the right order |
| **Re-evaluation required** | **Re-eval** | Something triggered a re-evaluation. Doesn't count until cleared | Arrange it; an Admin clears it |
| **Awaiting verification** | **Pending** | Recorded but not yet checked by the office. Doesn't count | Look at the card and press **Verify** |

An expired card shows **Expired** even if a prerequisite is also out of order, because it must be renewed either way.

## Requirement outcomes

| Outcome | Grid | Means | What to do |
| --- | --- | --- | --- |
| **Met** | **OK** | Holds the card and it counts | Nothing |
| **Met by an equivalent** | **Equiv** | Holds something that counts instead, such as OSHA 30 for OSHA 10 | Nothing |
| **Expiring soon** | **Soon** | Met, but the card is near expiry | Book the renewal |
| **Expired** | **Expired** | The card has lapsed | Renew it |
| **Blocked** | **Blocked** | Holds the card but it doesn't count yet | See the card's status above |
| **Missing** | **Missing** | Nothing that counts is on file. A card awaiting verification doesn't count | Add the card, or verify it |
| **New hire — in grace period** | **Grace** | Missing, but still inside the requirement's new-hire grace days | Get it done before the grace ends |

## Pending and Verified

Cards the office adds are saved as verified. **Awaiting verification** is for a card that was recorded but not yet checked by the office; you will rarely see it today.

## Medical cards

Below Admin, medical (restricted) types show the status only — no dates, numbers or notes.`,
  },
]
